class BeforeAfterCardsSlider {
    constructor(element) {
        this.slider = element;
        this.sectionId = this.slider.dataset.sectionId;
        this.cards = this.slider.querySelectorAll('.before-after-card');
        this.prevBtn = this.slider.parentElement.querySelector('.before-after-cards__nav-btn--prev');
        this.nextBtn = this.slider.parentElement.querySelector('.before-after-cards__nav-btn--next');
        this.dots = this.slider.parentElement.querySelectorAll('.before-after-cards__dot');

        // Check if autoplay is disabled from section settings
        const section = this.slider.closest('.before-after-cards-section');
        this.autoplayDisabled = section && (section.dataset.disableAutoplay === 'true' || section.dataset.disableAutoplay === true);
        console.log('Autoplay disabled:', this.autoplayDisabled, 'Value:', section?.dataset.disableAutoplay);

        this.currentIndex = 0;
        this.isAnimating = false;
        this.autoplayInterval = null;
        this.autoplayDelay = 5000; // 5 seconds

        // Touch/swipe properties
        this.startX = 0;
        this.startY = 0;
        this.endX = 0;
        this.endY = 0;
        this.minSwipeDistance = 50;

        this.init();
    }

    init() {
        if (this.cards.length <= 1) return;

        this.setupCards();
        this.bindEvents();

        // Only start autoplay if not disabled
        if (!this.autoplayDisabled) {
            this.startAutoplay();
        }

        // Apply custom background colors
        this.applyCustomBackgrounds();
    }

    setupCards() {
        this.cards.forEach((card, index) => {
            card.style.display = 'none';
            card.classList.remove('active');
            if (index === 0) {
                card.style.display = 'block';
                card.classList.add('active');
            }
        });
    }

    applyCustomBackgrounds() {
        this.cards.forEach((card) => {
            const blockId = card.dataset.blockId;
            const cardSettings = window.Shopify?.theme?.settings?.blocks?.[blockId];
            if (cardSettings?.card_background) {
                card.style.setProperty('--card-bg-color', cardSettings.card_background);
                card.style.background = cardSettings.card_background;
            }
        });
    }

    bindEvents() {
        // Navigation buttons
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', () => this.goToPrevious());
        }

        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', () => this.goToNext());
        }

        // Dot navigation
        this.dots.forEach((dot, index) => {
            dot.addEventListener('click', () => this.goToSlide(index));
        });

        // Touch events for swipe
        this.slider.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
        this.slider.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: true });
        this.slider.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });

        // Mouse events for desktop swipe
        this.slider.addEventListener('mousedown', (e) => this.handleMouseDown(e));
        this.slider.addEventListener('mousemove', (e) => this.handleMouseMove(e));
        this.slider.addEventListener('mouseup', (e) => this.handleMouseUp(e));
        this.slider.addEventListener('mouseleave', (e) => this.handleMouseUp(e));

        // Keyboard navigation
        this.slider.addEventListener('keydown', (e) => this.handleKeydown(e));

        // Pause autoplay on hover
        this.slider.parentElement.addEventListener('mouseenter', () => this.pauseAutoplay());
        this.slider.parentElement.addEventListener('mouseleave', () => {
            if (!this.autoplayDisabled) {
                this.startAutoplay();
            }
        });

        // Pause autoplay on focus
        this.slider.addEventListener('focusin', () => this.pauseAutoplay());
        this.slider.addEventListener('focusout', () => {
            if (!this.autoplayDisabled) {
                this.startAutoplay();
            }
        });
    }

    // Touch event handlers
    handleTouchStart(e) {
        this.startX = e.touches[0].clientX;
        this.startY = e.touches[0].clientY;
        this.pauseAutoplay();
    }

    handleTouchMove(e) {
        if (!this.startX || !this.startY) return;

        this.endX = e.touches[0].clientX;
        this.endY = e.touches[0].clientY;
    }

    handleTouchEnd(e) {
        if (!this.startX || !this.endX) return;

        const deltaX = this.startX - this.endX;
        const deltaY = Math.abs(this.startY - this.endY);

        // Only trigger swipe if horizontal movement is greater than vertical
        if (Math.abs(deltaX) > this.minSwipeDistance && Math.abs(deltaX) > deltaY) {
            if (deltaX > 0) {
                this.goToNext(); // Swipe left - go to next
            } else {
                this.goToPrevious(); // Swipe right - go to previous
            }
        }

        this.resetSwipeValues();
        if (!this.autoplayDisabled) {
            this.startAutoplay();
        }
    }

    // Mouse event handlers for desktop
    handleMouseDown(e) {
        this.startX = e.clientX;
        this.startY = e.clientY;
        this.slider.style.cursor = 'grabbing';
        this.pauseAutoplay();
    }

    handleMouseMove(e) {
        if (!this.startX) return;
        this.endX = e.clientX;
        this.endY = e.clientY;
    }

    handleMouseUp(e) {
        if (!this.startX || !this.endX) {
            this.resetSwipeValues();
            this.slider.style.cursor = 'grab';
            if (!this.autoplayDisabled) {
                this.startAutoplay();
            }
            return;
        }

        const deltaX = this.startX - this.endX;
        const deltaY = Math.abs(this.startY - this.endY);

        if (Math.abs(deltaX) > this.minSwipeDistance && Math.abs(deltaX) > deltaY) {
            if (deltaX > 0) {
                this.goToNext();
            } else {
                this.goToPrevious();
            }
        }

        this.resetSwipeValues();
        this.slider.style.cursor = 'grab';
        if (!this.autoplayDisabled) {
            this.startAutoplay();
        }
    }

    resetSwipeValues() {
        this.startX = 0;
        this.startY = 0;
        this.endX = 0;
        this.endY = 0;
    }

    handleKeydown(e) {
        switch (e.key) {
            case 'ArrowLeft':
                e.preventDefault();
                this.goToPrevious();
                break;
            case 'ArrowRight':
                e.preventDefault();
                this.goToNext();
                break;
        }
    }

    goToNext() {
        if (this.isAnimating) return;

        // Always loop - if at last slide, go to first slide
        const nextIndex = this.currentIndex === this.cards.length - 1 ? 0 : this.currentIndex + 1;
        this.goToSlide(nextIndex);
    }

    goToPrevious() {
        if (this.isAnimating) return;

        // Always loop - if at first slide, go to last slide
        const prevIndex = this.currentIndex === 0 ? this.cards.length - 1 : this.currentIndex - 1;
        this.goToSlide(prevIndex);
    }

    goToSlide(index) {
        if (this.isAnimating || index === this.currentIndex) return;

        this.isAnimating = true;
        const currentCard = this.cards[this.currentIndex];
        const nextCard = this.cards[index];

        // Fade out current card
        currentCard.classList.add('animating-out');

        setTimeout(() => {
            // Hide current card and show next card
            currentCard.style.display = 'none';
            currentCard.classList.remove('active', 'animating-out');

            nextCard.style.display = 'block';
            nextCard.classList.add('animating-in');

            setTimeout(() => {
                nextCard.classList.add('active');
                nextCard.classList.remove('animating-in');

                this.currentIndex = index;
                this.updateDots();
                this.isAnimating = false;
            }, 50);
        }, 300);
    }

    updateDots() {
        this.dots.forEach((dot, index) => {
            dot.classList.toggle('active', index === this.currentIndex);
        });
    }

    startAutoplay() {
        if (this.cards.length <= 1) {
            console.log('Cannot start autoplay: only', this.cards.length, 'cards');
            return;
        }
        if (this.autoplayDisabled) {
            console.log('Autoplay disabled');
            return;
        }

        console.log('Starting autoplay');
        this.pauseAutoplay();
        this.autoplayInterval = setInterval(() => {
            console.log('Autoplay tick - going to next');
            this.goToNext();
        }, this.autoplayDelay);
    }

    pauseAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
    }

    destroy() {
        this.pauseAutoplay();
        // Remove event listeners if needed
    }
}

// Initialize sliders when DOM is loaded
document.addEventListener('DOMContentLoaded', function () {
    const sliders = document.querySelectorAll('.before-after-cards__slider');

    sliders.forEach((slider) => {
        slider.sliderInstance = new BeforeAfterCardsSlider(slider);
    });
});

// Handle Shopify section reloads (theme editor)
document.addEventListener('shopify:section:load', function (event) {
    if (event.detail.sectionId) {
        const slider = event.target.querySelector('.before-after-cards__slider');
        if (slider) {
            slider.sliderInstance = new BeforeAfterCardsSlider(slider);
        }
    }
});

// Intersection Observer for performance (pause autoplay when not visible)
if ('IntersectionObserver' in window) {
    const observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            const slider = entry.target.querySelector('.before-after-cards__slider');
            if (slider && slider.sliderInstance) {
                if (entry.isIntersecting && !slider.sliderInstance.autoplayDisabled) {
                    slider.sliderInstance.startAutoplay();
                } else {
                    slider.sliderInstance.pauseAutoplay();
                }
            }
        });
    }, observerOptions);

    // Observe all before-after card sections
    document.querySelectorAll('.before-after-cards-section').forEach((section) => {
        observer.observe(section);
    });
}
